import { type HttpErrorResponse } from '@angular/common/http';

import type { ApiErrorItem } from '../http/api-envelope';

export type FieldErrors = Readonly<Record<string, readonly string[]>>;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
    readonly fieldErrors: FieldErrors = {},
    readonly traceId: string | null = null,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static isApiError(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }

  static fromHttp(response: HttpErrorResponse): ApiError {
    const items = readErrorItems(response.error);
    const first = items[0];
    const code = first?.error ?? null;
    const message = first?.detail || first?.message || response.message;

    return new ApiError(
      response.status,
      message,
      code,
      readFieldErrors(response.error),
      readTraceId(response.error),
      response,
    );
  }
}

function readErrorItems(body: unknown): readonly ApiErrorItem[] {
  if (!isRecord(body)) {
    return [];
  }

  const { errors } = body;

  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.filter(isRecord).map((entry) => ({
    error: typeof entry['error'] === 'string' ? entry['error'] : '',
    message: typeof entry['message'] === 'string' ? entry['message'] : undefined,
    detail: typeof entry['detail'] === 'string' ? entry['detail'] : undefined,
  }));
}

function readTraceId(body: unknown): string | null {
  if (!isRecord(body) || !isRecord(body['meta'])) {
    return null;
  }

  const traceId = body['meta']['trace_id'];

  return typeof traceId === 'string' ? traceId : null;
}

function readFieldErrors(body: unknown): FieldErrors {
  if (!isRecord(body) || !isRecord(body['fields'])) {
    return {};
  }

  const result: Record<string, readonly string[]> = {};

  for (const [field, messages] of Object.entries(body['fields'])) {
    if (Array.isArray(messages)) {
      result[field] = messages.filter((entry): entry is string => typeof entry === 'string');
    } else if (typeof messages === 'string') {
      result[field] = [messages];
    }
  }

  return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
